import React, { useState, useEffect, useRef } from "react";
import EHCPUploader from "./EHCPUploader";
import EHCPIngestionTimeline from "./EHCPIngestionTimeline";
import EHCPExtractedTargets from "./EHCPExtractedTargets";
import EHCPSectionF from "./EHCPSectionF";
import SectionFProvisionPanel from "./SectionFProvisionPanel";
import { Icons } from "../lib/Icons";
import { getStudentEhcpStoragePath } from "../lib/ehcpPath";
import { studentHasEhcp } from "../lib/students";

const EHCPManager = ({ student, studentId, onEhcpStatusChange }) => {
  const hasEhcp = studentHasEhcp(student);
  const [activeTab, setActiveTab] = useState("upload");
  const [processingComplete, setProcessingComplete] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  // Bumped after the user saves the four-area Section F textareas or accepts
  // a proposal, so SectionFProvisionPanel re-fetches its gap analysis.
  const [provisionRefreshKey, setProvisionRefreshKey] = useState(0);
  const [uploadBusy, setUploadBusy] = useState(false);
  const bumpProvisionRefresh = () => setProvisionRefreshKey((k) => k + 1);
  // Track whether the user has manually selected a tab so live Firestore
  // updates to `student` don't snap them back to a default tab.
  const userSelectedTabRef = useRef(false);
  // Reset the manual-selection flag when switching to a different student.
  const lastStudentIdRef = useRef(studentId);
  if (lastStudentIdRef.current !== studentId) {
    lastStudentIdRef.current = studentId;
    userSelectedTabRef.current = false;
  }

  const selectTab = (tab) => {
    userSelectedTabRef.current = true;
    setActiveTab(tab);
  };

  useEffect(() => {
    if (processingComplete) {
      setShowSuccessMessage(true);
      userSelectedTabRef.current = true;
      const timer = setTimeout(() => {
        setActiveTab("targets");
        setShowSuccessMessage(false);
        setProcessingComplete(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [processingComplete]);

  useEffect(() => {
    // Respect any tab the user has explicitly clicked, or stay on upload while processing.
    if (userSelectedTabRef.current || uploadBusy) return;
    if (!hasEhcp) {
      setActiveTab("upload");
      return;
    }
    if (getStudentEhcpStoragePath(student) || student?.ehcpDetails?.sectionE) {
      setActiveTab("targets");
    } else {
      setActiveTab("upload");
    }
  }, [student, hasEhcp, uploadBusy]);

  const handleProcessingComplete = () => {
    setProcessingComplete(true);
  };

  const handleSuccessMessageClick = () => {
    selectTab("targets");
    setShowSuccessMessage(false);
  };

  if (!hasEhcp) {
    return (
      <div className="space-y-4">
        <EHCPUploader
          studentId={studentId}
          onProcessingComplete={handleProcessingComplete}
          onEhcpStatusChange={onEhcpStatusChange}
          onBusyChange={setUploadBusy}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showSuccessMessage && (
        <div
          className="p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer hover:bg-green-100 transition-colors duration-200"
          onClick={handleSuccessMessageClick}
        >
          <div className="flex items-center text-green-700">
            <Icons.CheckCircle className="w-5 h-5 mr-2" />
            <span className="font-medium">EHCP targets extracted. Click here to view them</span>
          </div>
        </div>
      )}

      <div className="flex border-b border-gray-200" role="tablist" aria-label="EHCP tabs">
        <button
          onClick={() => selectTab("upload")}
          className={`px-4 py-2 font-medium text-sm transition-colors duration-200 ${
            activeTab === "upload"
              ? "border-b-2 border-blue-500 text-blue-600 bg-blue-50"
              : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
          }`}
          role="tab"
          aria-selected={activeTab === 'upload'}
          aria-controls="ehcp-panel-upload"
          id="ehcp-tab-upload"
        >
          <Icons.Upload className="w-4 h-4 mr-1 inline-block" /> Upload & Process
        </button>
        <button
          onClick={() => selectTab("targets")}
          className={`px-4 py-2 font-medium text-sm transition-colors duration-200 ${
            activeTab === "targets"
              ? "border-b-2 border-blue-500 text-blue-600 bg-blue-50"
              : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
          }`}
          role="tab"
          aria-selected={activeTab === 'targets'}
          aria-controls="ehcp-panel-targets"
          id="ehcp-tab-targets"
        >
          EHCP (Section E)
        </button>
        <button
          onClick={() => selectTab("provision")}
          className={`px-4 py-2 font-medium text-sm transition-colors duration-200 ${
            activeTab === "provision"
              ? "border-b-2 border-indigo-500 text-indigo-600 bg-indigo-50"
              : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
          }`}
          role="tab"
          aria-selected={activeTab === 'provision'}
          aria-controls="ehcp-panel-provision"
          id="ehcp-tab-provision"
        >
          Section F
        </button>
      </div>

      <div className="min-h-[200px]">
        {activeTab === "upload" && (
          <div className="space-y-4" role="tabpanel" id="ehcp-panel-upload" aria-labelledby="ehcp-tab-upload">
            <EHCPUploader
              studentId={studentId}
              onProcessingComplete={handleProcessingComplete}
              onEhcpStatusChange={onEhcpStatusChange}
              onBusyChange={setUploadBusy}
            />
            <EHCPIngestionTimeline studentId={studentId} schoolId={student?.schoolId} />
          </div>
        )}

        {activeTab === "targets" && (
          <div className="space-y-4" role="tabpanel" id="ehcp-panel-targets" aria-labelledby="ehcp-tab-targets">
            <EHCPExtractedTargets
              student={student}
              onProcessingComplete={handleProcessingComplete}
              hideSuccessMessages={true}
            />
          </div>
        )}
        {activeTab === "provision" && (
          <div className="space-y-4" role="tabpanel" id="ehcp-panel-provision" aria-labelledby="ehcp-tab-provision">
            <EHCPSectionF studentId={studentId} onSaved={bumpProvisionRefresh} />
            {/* Gap analysis: which EHCP-mandated provisions aren't yet recorded
                as active studentProvisions / interventions. Also feeds the
                school-wide SencoProvisionAlerts dashboard widget. */}
            <SectionFProvisionPanel
              key={`provision-gap-${provisionRefreshKey}`}
              studentId={studentId}
              student={student}
              filterDestination="all"
              showUnmet={true}
              onAccepted={() => {
                bumpProvisionRefresh();
                onEhcpStatusChange?.();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EHCPManager;
